/**
 * Help article registry — the curated knowledge base for ship vetters
 * and operators using VarunAIq. Articles are tree-shakeable: import the
 * registry once and Webpack drops anything you don't reference.
 *
 * Authoring guidelines
 *   - Punchy, inspector-focused prose. UK English.
 *   - Never reproduce CDI/SIRE PDFs verbatim. Paraphrase, link out.
 *   - IMO conventions are public domain — quote briefly with citation.
 *   - Keep a "lastReviewed" date on every article.
 *
 * The MarkdownLite renderer supports: # / ## / ###, **bold**, *italic*,
 * `code`, ```fenced```, lists (- and 1.), [links](…), `> blockquote`,
 * `---` HR. No raw HTML.
 */

export interface HelpArticle {
  slug: string;
  topic: HelpTopic;
  title: string;
  summary: string;
  contexts?: string[];
  /** Lucide icon name (e.g. 'Anchor', 'Compass'). Imported lazily by the drawer. */
  icon: string;
  body: string;
  imoRefs?: string[];
  related?: string[];
  audience?: 'inspector' | 'shipmgr' | 'admin' | 'all';
  lastReviewed?: string;
  sources?: { label: string; url: string }[];
}

export type HelpTopic =
  | 'getting-started'
  | 'pre-inspection'
  | 'on-board'
  | 'observations-evidence'
  | 'reporting-signing'
  | 'standards-imo'
  | 'standards-vetting'
  | 'glossary'
  | 'app-features'
  | 'troubleshooting'
  | 'compliance-ethics';

export const TOPIC_META: Record<HelpTopic, { label: string; icon: string; description: string }> = {
  'getting-started':       { label: 'Getting started',       icon: 'Sparkles',   description: 'Your first 5 minutes in VarunAIq.' },
  'pre-inspection':        { label: 'Pre-inspection',        icon: 'ClipboardList', description: 'Briefing, document intake, prep workspace.' },
  'on-board':              { label: 'On-board',              icon: 'Ship',       description: 'Section navigation, answers, smart help.' },
  'observations-evidence': { label: 'Observations & evidence', icon: 'Camera',   description: 'No answers, voice notes, photo capture.' },
  'reporting-signing':     { label: 'Reporting & signing',   icon: 'PenLine',    description: 'Section B, AI draft, signatures, submission.' },
  'standards-imo':         { label: 'IMO standards',         icon: 'Shield',     description: 'SOLAS, MARPOL, ISM, ISPS, MLC, STCW, ISGOTT.' },
  'standards-vetting':     { label: 'Vetting standards',     icon: 'Award',      description: 'CDI, SIRE, OCIMF, HVPQ, S/R/D categories.' },
  'glossary':              { label: 'Glossary',              icon: 'BookOpen',   description: 'Maritime acronyms and terms in plain English.' },
  'app-features':          { label: 'App features',          icon: 'Wand2',      description: 'AI draft, offline mode, cert OCR, T&Cs.' },
  'troubleshooting':       { label: 'Troubleshooting',       icon: 'AlertTriangle', description: 'When something goes wrong.' },
  'compliance-ethics':     { label: 'Compliance & ethics',   icon: 'Scale',      description: 'Observer-only voice, confidentiality.' },
};

// ─── Articles ────────────────────────────────────────────────────────────────

export const ARTICLES: HelpArticle[] = [
  {
    slug: 'getting-started/welcome',
    topic: 'getting-started',
    title: 'Welcome to VarunAIq',
    summary: 'What this app does and how it saves you 3+ hours per inspection.',
    icon: 'Sparkles',
    audience: 'all',
    lastReviewed: '2026-05-05',
    contexts: ['vetting/home', 'vetting/demo'],
    body: `
VarunAIq is a workbench for ship inspectors and vetting operators. It pulls together everything you need to prepare, conduct, and submit a CDI or RISQ inspection on one screen — plus TMSA self-assessment for the office.

## What you get

- **Pre-inspection briefing** auto-loaded from the VarunAIq maritime intelligence platform: AIS history, port calls, PSC inspections, sanctions, prior reports, classification — and live weather at the vessel's position.
- **On-board questionnaires**: the full CDI 10.5 SIR (sections O–14, ~970 questions with the type-specific Chapter 5) and RightShip RISQ 3.2 (dry bulk / general cargo / container) — offline-capable; syncs when you're back online.
- **Smart help** at the question level: SOLAS / MARPOL / ISGOTT references, prior answers for this vessel, similar past observations across the industry.
- **AI draft** that compiles your answers into the operator's preferred report format. You verify every line before signing — observer-only voice is enforced.
- **Captain countersign** + submission to the operator in CDI/RISQ-compatible output.
- **Self-inspections** for the shore office: run the same question bank internally (ISM Code §12 internal-audit cadence tracked on the fleet dashboard); every scoreable No becomes a tracked corrective action.

## Your first inspection

1. Tap **Start an inspection** on the home page.
2. Pick the vessel (live AIS search) — the framework (CDI or RISQ) and the type-specific profile are suggested from the vessel's registered type.
3. Review the auto-loaded briefing.
4. On boarding day, tap **Start inspection** to enter the on-board flow.
5. Work through the sections; smart help has your back on every question.
6. End of day → review observations → generate AI draft → sign + countersign → submit.

## What's next

Read **Pre-inspection briefing** to understand the data behind the briefing card.
`,
    related: ['pre-inspection/briefing', 'on-board/section-navigation', 'compliance-ethics/observer-only'],
  },
  {
    slug: 'getting-started/account-roles',
    topic: 'getting-started',
    title: 'Roles & permissions',
    summary: 'Admin, inspector, viewer — what each can do.',
    icon: 'Users',
    audience: 'all',
    lastReviewed: '2026-05-05',
    body: `
VarunAIq has three roles, set per-user in your identity provider (Clerk).

| Role | Map | Vetter | Admin panel | User mgmt |
|---|---|---|---|---|
| **Admin** | ✓ full | ✓ full | ✓ | ✓ |
| **Inspector** | ✓ full | ✓ full | — | — |
| **Viewer** | ✓ read-only | demo only | — | — |

Roles flow from the Clerk JWT (\`publicMetadata.role\`) into the app on every request — there's no separate Supabase mirror to keep in sync.

If you can't see the inspections workflow but should, sign out and back in to refresh your token. Still nothing? Ask a super admin to check your role under Admin → Users.
`,
  },
  {
    slug: 'pre-inspection/briefing',
    topic: 'pre-inspection',
    title: 'Vessel briefing — what each panel means',
    summary: 'AIS, PSC, sanctions, risk score, prior report — what to look for.',
    icon: 'Anchor',
    audience: 'inspector',
    lastReviewed: '2026-05-05',
    contexts: ['vetting/inspection-prep'],
    body: `
The briefing card is the "everything you need before you board" snapshot. Each panel is auto-loaded from the platform — no manual entry.

## AIS — last 90 days

Sparkline of position broadcasts over the last 90 days plus the trade region. Look for **gaps** (>24 h without a position when the vessel was at sea) — they correlate with AIS off-time, which can be a Q1.1.x deficiency.

## Last 5 port calls

Most recent arrivals/departures from the platform's port-call inference. Use this to anticipate cargo type and validate the vessel's stated trade pattern against question A.2 (operations during inspection).

## PSC inspections

Last 10 PSC visits. Pay attention to:
- **Detentions** in the last 12 months → likely subject of operator follow-up
- **Frequent deficiencies** at the same code → systemic issue
- **MoU diversity** — vessel inspected by Paris, Tokyo, Black Sea MoUs all clean is a good signal

## Sanctions

OFAC + EU + UK + UN screened against the vessel and its registered owner / manager. Anything other than "cleared" is a hard stop — escalate to your nominator before boarding.

## Risk score

Composite (0-100, lower = better) blending: owner sanctions, PSC history, class compliance, vessel age, AIS gaps. The drivers panel shows what nudged the score. Low / Medium / High / Critical bands map to the operator's vetting threshold.

## Prior CDI / SIRE report

If the operator uploaded a prior report, the briefing shows the date, total observations, closure rate, and the most cited questions. The on-board run page surfaces this same data per question — see **Smart help: prior answers**.

## Classification

Last drydock date, current class status (in / out of class), conditions of class (CoC) outstanding. Cross-check against question 1.4.x (Surveys & Drydocking) — operators often forget to update HVPQ when conditions clear.
`,
    related: ['standards-vetting/cdi-10.5-overview', 'on-board/smart-help'],
  },
  {
    slug: 'pre-inspection/cert-extraction',
    topic: 'pre-inspection',
    title: 'Certificate OCR — auto-fill Section 1.1',
    summary: 'Photograph a paper cert, app extracts type + expiry + issuer.',
    icon: 'Camera',
    audience: 'inspector',
    lastReviewed: '2026-05-05',
    contexts: ['vetting/inspection-prep'],
    body: `
Tap **Add evidence → Certificate** on the prep page. Take a photo of any of these:

- IOPP, IAPP, IEEC, IPP NLS
- ISM DOC, ISM SMC, ISSC, MLC
- IBC / IGC
- Minimum Safe Manning Document
- Class certificate
- Loadline / Tonnage
- Cargo Ship Safety Construction / Equipment / Radio

The certificate OCR pipeline returns:
- \`cert_type\`, \`certificate_no\`, \`vessel_imo\`, \`issue_date\`, \`expiry_date\`, \`issuing_authority\`

Tap **Apply pre-fills** — the related Section 1.1 questions get answered \`yes\` with an inspector remark documenting the source cert.

## Tips for clean extraction

- Flat surface, even lighting, no glare.
- Whole document in frame — corners visible.
- Stand directly above; avoid keystone angles.
- One cert per photo. Multi-page docs: shoot the cover + the validity page.
- If the model's confidence is low, the result panel highlights the field — you can edit before applying.

## What the AI cannot do

Read handwriting on amendment slips. Resolve heavily faded text. Verify whether the issuing authority is recognised by the flag state — that's still your call.
`,
    related: ['observations-evidence/photo-capture', 'standards-imo/ism-code'],
  },
  {
    slug: 'pre-inspection/offline-pack',
    topic: 'pre-inspection',
    title: 'Offline pack — work with no signal',
    summary: 'One-tap download of everything an inspection needs offline.',
    icon: 'Cloud',
    audience: 'inspector',
    lastReviewed: '2026-05-05',
    contexts: ['vetting/inspection-prep'],
    body: `
Tankers and offshore terminals routinely have no usable cellular signal. The offline pack is your contingency.

## What's in it

- Inspection metadata + the 697-question CDI 10.5 questionnaire
- The vessel briefing (positions, PSC, sanctions, port calls, classification)
- Existing responses (in case you've started already)
- 57 IMO regulation snippets (SOLAS, MARPOL, ISM, ISPS, MLC, STCW, ISGOTT)

The bundle is gzipped at the edge and stored in the browser's IndexedDB. Fetched once with an ETag so re-downloads are zero-cost when nothing changed.

## When to download it

The morning of the inspection while you still have wifi. The download takes 2-3 seconds.

## What happens offline

- All Yes / No / N/A taps are saved locally — they queue for sync.
- Photos and voice notes are stored locally and uploaded the moment connectivity returns.
- Smart help still works (IMO refs are bundled).
- Similar-observations RAG does NOT work (server-side); the panel hides itself.

## Conflict handling

If two devices edit the same response, last-write-wins per field. The app shows a small "synced N min ago" indicator so you can spot lag.
`,
    related: ['troubleshooting/sync-issues', 'on-board/smart-help'],
  },
  {
    slug: 'pre-inspection/tnc-templates',
    topic: 'pre-inspection',
    title: 'T&Cs templates — your clause library',
    summary: 'Render an email with vessel + port + date variables substituted.',
    icon: 'FileText',
    audience: 'inspector',
    lastReviewed: '2026-05-05',
    contexts: ['vetting/templates'],
    body: `
Every freelance CDI / SIRE inspector has their own Terms & Conditions. Varuna stores them as templates with variable substitution.

## How it works

1. Create a template under **Templates** with a body like:
   \`\`\`
   Subject: T&Cs for inspection of {{vessel_name}} at {{port}}
   Body:
   Dear Operator,
   Please find attached my Terms & Conditions for the inspection of
   M/V {{vessel_name}} (IMO {{vessel_imo}}) scheduled at {{port}} on
   {{scheduled_date}}.
   ...
   \`\`\`
2. From an inspection's prep page, tap **Send T&Cs** → pick template → review the rendered body → copy or auto-send.

## Variables available

- \`{{vessel_name}}\`
- \`{{vessel_imo}}\`
- \`{{port}}\`
- \`{{scheduled_date}}\`
- \`{{inspector_name}}\` (from your profile)
- \`{{today}}\` (today's ISO date)

Plus anything you supply in the additional-vars field at render time.

## Email send (optional)

If your admin has set the \`RESEND_API_KEY\` worker secret, **Auto-send** dispatches via Resend with a 5-second timeout. Otherwise the rendered text comes back and you copy-paste into your own client.
`,
  },
  {
    slug: 'on-board/section-navigation',
    topic: 'on-board',
    title: 'The CDI 10.5 sections — O to 14',
    summary: 'How questions are grouped, booklet order, and which icon means what.',
    icon: 'Compass',
    audience: 'inspector',
    lastReviewed: '2026-05-05',
    contexts: ['vetting/inspection-run'],
    body: `
The questionnaire follows the printed booklet's order: lettered front-matter first, then the 14 thematic chapters.

| § | Topic | Icon | Approx. Qs |
|---|---|---|---|
| O | Inspection Report Data (times, operator's rep, SID) | ClipboardList | 6 |
| A | General Information (ship, owner, manager fill-ins) | Info | 26 |
| B | Inspection Summary (closing meeting) | ClipboardList | 3 |
| 1 | Certification & Manning | BadgeCheck | 100 |
| 2 | Management & Personnel | Users | 31 |
| 3 | Bridge | Compass | 107 |
| 4 | Mooring | Link | 36 |
| 5C/5G/5P | Cargo — type-specific (Chemical 197 / LPG 209 / Product 182) | Package | ~200 |
| 6 | Engine Department | Cog | 72 |
| 7 | Operational Safety | HardHat | 42 |
| 8 | Health, Safety & PPE | HeartPulse | 109 |
| 9 | Firefighting | Flame | 65 |
| 10 | Lifesaving | LifeBuoy | 43 |
| 11 | Environmental Protection | Leaf | 92 |
| 12 | Security | Lock | 12 |
| 13 | Hull & Superstructure | LayoutGrid | 8 |
| 14 | Accommodation | BedDouble | 10 |

The tab strip at the top of the run page lets you jump directly; inside a section, booklet subsection dividers ("1.1 · Ship Certification") keep your place. Each section has a progress chip showing answered / total and the count of observations.

## Conditional questions

Booklet "If Yes: / If No:" sub-questions stay dimmed — marked *not answerable* — until the parent question has the triggering answer, exactly like the printed report.

## Cargo variant selection

Chapter 5 is type-specific: the CDI 10th Edition ships separate 5C (Chemical), 5G (LPG) and 5P (Product) questionnaires. When you create an inspection the platform reads the vessel's registered \`vessel_type\` and pre-selects the matching variant — override it on the New Inspection form if the ship's Certificate of Fitness says otherwise. The run page then shows the core chapters plus only your variant's Section 5.
`,
    related: ['standards-vetting/cdi-10.5-overview', 'standards-vetting/category-S-R-D-NS'],
  },
  {
    slug: 'on-board/answering-yes-no-na',
    topic: 'on-board',
    title: 'Yes / No / N/A / N/V — what each means',
    summary: 'Answer semantics, fill-in fields, bulk answering, and what triggers an observation.',
    icon: 'Check',
    audience: 'inspector',
    lastReviewed: '2026-05-05',
    contexts: ['vetting/inspection-run'],
    body: `
Three answers on CDI questions — four on RISQ — and one scoring rule.

## Yes

The vessel / crew / system meets the standard described in the question. No further action.

## No

The vessel / crew / system does **not** meet the standard. This **automatically generates an observation** for any question with category Statutory (S), Recommended (R), or Desirable (D). You'll be required to add a remark before saving.

## N/A

The question doesn't apply to this vessel:
- Equipment-specific question and the vessel doesn't carry the equipment.
- Cargo-specific question and the vessel isn't carrying that cargo.
- Lead question for a sub-question chain that resolves elsewhere.

For non-scoring (NS) questions, "No" is informational only — no observation is auto-created. Read the question carefully; some NS questions are gating others.

## N/V — Not Verified (RISQ only)

RISQ questions carry a fourth checkbox: **N/V** means the item was not sighted during this inspection. It is neither a pass nor a deficiency and never creates an observation. Keyboard: v or 4.

## Fill-in questions

Dates, names, authorities and counts ("Issuing Authority:", "Date the vessel was delivered:") take a typed value instead of Yes/No — dates get a date picker, counts a numeric pad. A filled value counts as answered.

## Bulk answering

Vetting convention is *satisfactory unless recorded otherwise*. The toolbar above the question list can mark every **unanswered Yes/No question** in the section — or across all sections — as Yes in one confirmed tap. It never touches fill-ins, existing answers, or observations.

## When in doubt

If you're not sure whether to mark No or N/A, mark **No** and explain in the remark that the equipment / situation isn't applicable. The operator can challenge in their comments. False N/A is harder to defend than an explained No.

## What gets logged

Every change to an answer is timestamped and tied to your Clerk user ID. Once you sign the inspection, responses lock — you can't edit retrospectively without a sign reversal.
`,
    related: ['on-board/observations', 'standards-vetting/category-S-R-D-NS'],
  },
  {
    slug: 'on-board/observations',
    topic: 'observations-evidence',
    title: 'Writing a clean observation',
    summary: 'Voice-to-text, severity, what makes a defensible observation.',
    icon: 'AlertTriangle',
    audience: 'inspector',
    lastReviewed: '2026-05-05',
    contexts: ['vetting/inspection-run'],
    body: `
A "No" answer to a scoring question creates an observation. The operator will read every word — keep them tight, factual, and free of advice.

## The four pieces

Every observation should answer:
1. **What** was found (factual; observer voice)
2. **Where / when** (location on board, time)
3. **Evidence** (photo / voice memo / document — auto-attached)
4. **Standard** (the IMO / industry reference; auto-populated from the question's \`imo_refs\`)

## Phrasing — observer-only voice

You are an **observer**. You do not advise on corrections, recommend fixes, or suggest who should do what. The CDI Code of Conduct is explicit on this.

| ❌ Avoid | ✅ Prefer |
|---|---|
| "The crew should fix the…" | "The detector at frame 42 was found to be in fault on the panel." |
| "Recommend overhaul of…" | "Records did not evidence overhaul on schedule." |
| "Master to issue…" | "No master's standing order was sighted for this operation." |

## Voice-to-text

Tap the mic icon in the observation modal. The browser transcribes locally (Web Speech API, no audio leaves the device). Edit the text before saving.

## Severity

Pulled automatically from the question's category:
- **S — Statutory** (red, highest weight)
- **R — Recommended** (amber)
- **D — Desirable** (green/info)
- **NS — Non-scoring** (no observation generated)

You can't override severity; it's set by the questionnaire.
`,
    related: ['observations-evidence/photo-capture', 'compliance-ethics/observer-only'],
  },
  {
    slug: 'observations-evidence/photo-capture',
    topic: 'observations-evidence',
    title: 'Photo & voice evidence',
    summary: 'How evidence is auto-tagged with question ref + GPS + time.',
    icon: 'Camera',
    audience: 'inspector',
    lastReviewed: '2026-05-05',
    contexts: ['vetting/inspection-run'],
    body: `
Evidence files are uploaded to a private R2 bucket and linked to the question or observation that prompted them.

## Auto-tagging

Every photo / voice clip is stamped with:
- Question ref (e.g. \`7.1.23\`)
- Inspection ID (links into the audit trail)
- GPS coordinates (if you've granted location permission on the device)
- Capture timestamp (UTC)
- SHA-256 hash (for integrity)

The auto-tag means you don't have to remember which photo went with which question — the report PDF stitches them back automatically.

## Photo tips

- Use the **Camera** tab in the evidence drawer (calls \`<input capture="environment">\` so the OS opens the rear camera).
- Hold steady; the AI in subsequent passes can read text in the photo to corroborate your remark.
- For nameplate / serial number captures, get close — those are often what an operator disputes.

## Voice tips

The Voice tab uses the browser's MediaRecorder API. Audio is uploaded as-is and transcribed server-side later. **Do not** include third-party identifiable info in voice memos — they're confidential but the audio itself isn't redactable.

## Document upload

The Document tab takes PDFs / images of crew lists, work permits, log book pages. Same auto-tagging.
`,
    related: ['on-board/observations', 'pre-inspection/cert-extraction'],
  },
  {
    slug: 'reporting-signing/section-b',
    topic: 'reporting-signing',
    title: 'Section B — auto-collated observations',
    summary: 'How the summary is built and what you can edit.',
    icon: 'ClipboardCheck',
    audience: 'inspector',
    lastReviewed: '2026-05-05',
    contexts: ['vetting/inspection-review'],
    body: `
Section B is the operator's summary view. It's built automatically from every "No" you've recorded — no separate write-up step.

## What's collated

For each observation:
- Question ref + title
- Severity badge (S / R / D)
- Your remark (editable inline before signing)
- Linked evidence count (photos / voice)
- IMO references

## Editing

You can refine wording inline up until you sign. Once signed, the response is locked — any change requires explicit reversal by an admin and gets a full audit trail entry.

## What the operator sees

After you submit, the operator:
- Reads each observation
- Files a comment / root cause / preventive action / rectification
- Optionally attaches their own evidence (rectified pump photo, etc.)

Their comments appear inline in the final report under each observation, never as a rebuttal to yours. The platform doesn't allow back-and-forth threads — it's a one-shot exchange.
`,
    related: ['reporting-signing/ai-draft', 'reporting-signing/signatures'],
  },
  {
    slug: 'reporting-signing/ai-draft',
    topic: 'reporting-signing',
    title: 'AI-assisted draft — what it does and doesn\'t',
    summary: 'AI writes the first draft; you verify every line.',
    icon: 'Wand2',
    audience: 'inspector',
    lastReviewed: '2026-05-05',
    contexts: ['vetting/inspection-review'],
    body: `
Tap **Generate AI draft** on the review page. The platform compiles your answers and observations into a structured markdown report formatted for CDI / SIRE conventions.

## What the AI is told

System prompt explicitly forbids:
- Inventing observations not in your input
- Suggesting fixes / corrective actions
- Using prescriptive voice ("must", "should rectify")
- Advising the operator
- Omitting the \`[AI DRAFT — INSPECTOR TO VERIFY]\` header

Output structure: Executive Summary → Vessel Particulars → Inspection Context → Findings By Section → Detailed Observations → Positive Findings → Compliance Summary.

## What the AI is good at

- Tightening rambling voice notes into observer-style prose
- Surfacing patterns ("3 of 5 hot work permits showed the same gap")
- Producing CDI-compatible severity labels from your category tags

## What the AI is bad at

- Knowing facts about the vessel beyond what you provided
- Distinguishing remembered detail from fabrication — if a detail isn't in your remark, it won't appear in the draft
- Replacing your judgement on contestable findings

## Iteration

You can hit **Regenerate** any number of times before signing. Each regeneration uses the latest state of your responses + remarks. The draft text is also available for copy-out if you want to take it offline.

## Phase-2 RAG (optional)

If your admin has \`RAG_DRAFT_ENABLED=1\` set, the draft also pulls top-3 semantically similar prior remarks from your fleet's history (anonymised) as a tone reference. The AI is told to use them only as vocabulary guidance, never to copy verbatim.
`,
    related: ['compliance-ethics/observer-only', 'on-board/smart-help'],
  },
  {
    slug: 'standards-vetting/cdi-vs-sire',
    topic: 'standards-vetting',
    title: 'CDI · RISQ · SIRE — pick the right system',
    summary: 'Two industry inspection programmes; same vessel, different rules.',
    icon: 'Award',
    audience: 'all',
    lastReviewed: '2026-05-05',
    body: `
Most tanker inspections globally run through one of two systems:

| | **SIRE 2.0** | **CDI Marine** |
|---|---|---|
| Owner | OCIMF | Chemical Distribution Institute |
| Members | Oil majors (BP, Shell, Chevron, Repsol, …) | Chemical & gas producers (CEFIC core) |
| Scope | Crude / product tankers (chemicals + LPG / LNG added later) | Chemical / LPG / LNG / bulk |
| Volume | ~22,500 reports / year on ~8,000 tankers | ~2,500 reports / year |
| Inspectors | ~500 worldwide | ~90 worldwide |
| Validity | Per inspection (max 4 / vessel / year) | 12 months from inspection date |
| Initiator | Each oil major when they need the vessel | Ship manager via CDI website |

Same vessel can be SIRE-inspected by Shell on Tuesday and Chevron on Friday — that's normal. CDI is one inspection a year.

## Picking a questionnaire

VarunAIq ships **CDI 10.5** (sections O–14, ~970 questions with the type-specific Chapter 5 for chemical / LPG / product tankers) and **RightShip RISQ 3.2** (dry bulk, general cargo and container ships — cargo-operation profiles 8A–8F plus alternative-fuel, ice and STS modules) today. **TMSA 3** runs as a fleet-level self-assessment under Comply. **SIRE 2.0** preparation is on the roadmap pending an OCIMF licensing conversation — the question library is published, but commercial embedding needs their sign-off.

## Reports flow

After upload to the system database, member companies can pull the report and decide independently whether to vet the vessel. The inspector never sees who's actually using the report.
`,
    related: ['standards-vetting/cdi-10.5-overview', 'compliance-ethics/confidentiality'],
  },
  {
    slug: 'standards-vetting/category-S-R-D-NS',
    topic: 'standards-vetting',
    title: 'Categories: S, R, D, NS — and RISQ M/V',
    summary: 'Statutory / Recommended / Desirable / Non-scoring.',
    icon: 'Shield',
    audience: 'inspector',
    lastReviewed: '2026-05-05',
    body: `
Every CDI 10.5 question is tagged with one of four category codes. The category drives severity and scoring.

## S — Statutory

Anchored in an IMO convention or flag-state regulation. A "No" here is a hard regulatory shortfall. Operators take statutory observations the most seriously and rectification is rarely contested.

Visual: red badge.

## R — Recommended

From an industry code of practice (ISGOTT, COSWP, MEG, ISM industry guidance). Not a legal requirement but represents the bar set by the operator community.

Visual: amber badge.

## D — Desirable

CDI participants' shared aspiration — better-than-recommended. Often about culture, training depth, or maintenance hygiene rather than equipment.

Visual: green badge.

## NS — Non-scoring

Information collected but not weighted. Sometimes a lead question that gates whether subsequent questions apply. A "No" here is just a fact, not an observation.

Visual: grey badge.

## Inspector view

In the on-board UI, the badge is left of every question. The Section B summary groups observations by severity.
## RISQ's markers: M and V

RightShip categorises differently: **M** questions are answerable from documentation ahead of boarding (the basis of a remote pre-assessment), **V** questions must be verified physically on board. Combined "(V & M)" items resolve to V — verification wins.

`,
    related: ['on-board/answering-yes-no-na', 'standards-vetting/group-I-vs-SI'],
  },
  {
    slug: 'standards-vetting/group-I-vs-SI',
    topic: 'standards-vetting',
    title: 'Inspection vs Self-Inspection (I vs SI)',
    summary: 'How the 15% sample rule works on SI questions.',
    icon: 'Search',
    audience: 'inspector',
    lastReviewed: '2026-05-05',
    body: `
Each question is also tagged with a group code:

- **I — Inspection question.** You verify in person, every time.
- **SI — Self-Inspection question.** The operator pre-fills these in their Self-Inspection Document (SID).

## The 15% sample rule

For questions tagged SI, you randomly sample **15%** during a full inspection. If any of the sampled SI answers are wrong (operator answered Yes but reality says No), the inspection automatically reverts to a **full inspection** — every SI question becomes I.

## What this means in practice

- You cannot skip questions. SI tagged ≠ skip.
- The platform highlights which SI questions have been sampled in the current run; you can choose more if a pattern of operator over-claiming emerges.
- If the SID isn't provided pre-inspection, every question becomes I.

## Why this matters

The system was designed to reward honest self-inspection. Operators who mark Yes when reality says No risk a full inspection AND a credibility hit on the next round. That's why the 15% spot-check is the control.
## SI mode in the run page

Tap **SI only** in the run-page toolbar to filter the section to its Self-Inspection questions — the digital equivalent of the Online SID worksheet the crew completes before the inspector boards.

`,
    related: ['standards-vetting/cdi-10.5-overview', 'on-board/section-navigation'],
  },
  {
    slug: 'standards-imo/key-conventions',
    topic: 'standards-imo',
    title: 'The IMO conventions you cite most',
    summary: 'SOLAS, MARPOL, ISM, ISPS, MLC, STCW — at-a-glance.',
    icon: 'Globe',
    audience: 'all',
    lastReviewed: '2026-05-05',
    body: `
Six conventions cover ~95% of inspector citations. The platform's IMO reference library has 57 specific snippets indexed by chapter; tap any \`imo_refs\` chip on a question to view.

## SOLAS

International Convention for the Safety of Life at Sea (1974, as amended). 14 chapters covering construction, fire safety, life-saving, radio, nav safety, cargo, dangerous goods, nuclear ships, ISM Code, identification, security (XI-1, XI-2 / ISPS), bulk carriers.

## MARPOL

International Convention for the Prevention of Pollution from Ships. Six annexes:
I (oil), II (NLS), III (packaged HM), IV (sewage), V (garbage), VI (air pollution incl. EEDI / EEXI / CII).

## ISM Code

International Safety Management Code — Document of Compliance (DOC) for the company, Safety Management Certificate (SMC) for the ship. Inspector verifies both physically.

## ISPS Code

International Ship and Port Facility Security Code. Parts A (mandatory) and B (guidance). Ship Security Certificate (ISSC), Continuous Synopsis Record (CSR).

## MLC 2006

Maritime Labour Convention. Crew welfare standard — accommodation, hours of rest, medical, complaints procedure.

## STCW

Standards of Training, Certification and Watchkeeping for Seafarers. Manila amendments revised it in 2010. Inspectors verify watchkeeper certificates plus actual rest-hour records.

## ISGOTT

International Safety Guide for Oil Tankers and Terminals (industry, not IMO). The bible for tanker operations.

## COSWP

Code of Safe Working Practices for Merchant Seafarers (UK MCA). Cited frequently by Paris MoU PSC.

Tap any reference chip in the questionnaire to see its summary; tap **Read full text →** to jump to the IMO source.
`,
    sources: [
      { label: 'IMO conventions index', url: 'https://www.imo.org/en/About/Conventions/Pages/Default.aspx' },
    ],
    related: ['standards-vetting/cdi-vs-sire'],
  },
  {
    slug: 'compliance-ethics/observer-only',
    topic: 'compliance-ethics',
    title: 'Observer-only voice — the cardinal rule',
    summary: 'Inspectors observe. They do not advise, operate, or recommend.',
    icon: 'Eye',
    audience: 'inspector',
    lastReviewed: '2026-05-05',
    body: `
Both CDI and SIRE require inspectors to be observers only. The platform enforces this everywhere — system prompts, question wording, even the AI draft.

## What you must NOT do on board

- Operate any equipment yourself (you may *request* it be operated for test).
- Advise the master / crew / shore staff on how to fix anything.
- Pass opinions, in writing or verbally, on the standard of any item.
- Suggest corrective actions in your remarks.
- Discuss the contents of any prior CDI / SIRE report with the master.
- Become party to any conversation between the ship, terminal, or port authority.

## What you must do

- Identify yourself with proper credentials on boarding.
- Wear PPE as required for the location.
- Comply with the ship's safety procedures and posted notices.
- Get permission from the master before entering restricted areas.
- For enclosed-space entry: follow Tanker Safety Guide (Chemicals) Chapter 3 fully.

## In remarks

Use observer language: "was found", "was sighted", "no record was produced for", "could not be evidenced".

Avoid: "should", "must", "needs to", "is recommended", "ought to".

The AI draft generator is told the same thing in its system prompt. If you spot prescriptive voice in a draft, regenerate or edit before signing.

## CDI Code of Conduct

Inspectors are bound by the CDI Code of Business Conduct in all dealings with stakeholders. Findings and operator comments are confidential and the property of the Ship Operator. **Do not** discuss the contents of the report with any third party.
`,
    sources: [
      { label: 'CDI inspector guidelines', url: 'https://www.cdim.org' },
      { label: 'OCIMF SIRE 2.0', url: 'https://www.ocimf.org/programmes/sire-2-0' },
    ],
    related: ['compliance-ethics/confidentiality', 'reporting-signing/ai-draft'],
  },
  {
    slug: 'compliance-ethics/confidentiality',
    topic: 'compliance-ethics',
    title: 'Confidentiality of reports',
    summary: 'Who sees what, and what stays sealed.',
    icon: 'Lock',
    audience: 'inspector',
    lastReviewed: '2026-05-05',
    body: `
The inspection report is the property of the Ship Operator. Inspectors cannot share it with third parties.

## Who sees the report

- **Ship Operator** — full read + comment access.
- **Vetting system members** (CDI participants / OCIMF members) — read access via their database subscription.
- **Inspector who wrote it** — read access; cannot initiate discussion of operator comments.
- **Nobody else.**

## What stays out of the report

- Personal information about specific crew members beyond what the manning matrix already publishes.
- Photos that identify named crew unless related to a documented observation.
- Any opinion on the master's competence (that's not the inspector's call — STCW certification is).

## Within Varuna

Each inspection's data is scoped to the inspector who created it (admins can see all). The smart help "similar prior observations" panel anonymises vessel names by default at pilot scale; multi-tenant isolation lands when the platform serves more than one operator.

## When in doubt

Default to confidentiality. If the operator asks to see the draft mid-inspection — politely decline; the report goes through the system's submission flow, not directly.
`,
    related: ['compliance-ethics/observer-only', 'reporting-signing/section-b'],
  },
  {
    slug: 'troubleshooting/sync-issues',
    topic: 'troubleshooting',
    title: 'Sync didn\'t complete — what to do',
    summary: 'Recovery steps when an answer or photo fails to upload.',
    icon: 'RefreshCcw',
    audience: 'inspector',
    lastReviewed: '2026-05-05',
    body: `
The platform is offline-first. Every Yes / No / N/A and every photo is queued locally before being sent to the server.

## Symptoms

- Status bar shows "X items pending sync"
- Photos appear with a small clock icon overlay
- The Save button shows a spinner that doesn't resolve

## Triage

1. **Connectivity check.** Open any web page in your phone's browser. If the wider internet is fine, the issue is at our end.
2. **Force sync.** Pull down on the responses list — the queued ops post in batches.
3. **Inspect queue.** Tap **Settings → Pending operations** to see exactly what's stuck.
4. **Retry single.** Tap any pending row and hit Retry; you'll see the actual error.

## Common errors

- \`401 Unauthorized\` → your session expired. Sign out, sign back in. Pending ops survive the sign-out.
- \`409 Conflict\` → another device edited the same response. Conflict UI lets you pick which version wins.
- \`413 Payload Too Large\` → the photo is bigger than 25 MB. Re-take with a smaller resolution.
- \`429 Too Many Requests\` → server busy. Wait 30 s; queue retries automatically with backoff.

## Last resort

Pending ops persist in IndexedDB. Even if you close the browser, they're waiting. **Don't** clear browser data mid-inspection — that's the one thing that loses queued work.
`,
    related: ['pre-inspection/offline-pack'],
  },
  {
    slug: 'app-features/keyboard-shortcuts',
    topic: 'app-features',
    title: 'Keyboard shortcuts',
    summary: 'Faster workflow once your hands know where the keys are.',
    icon: 'Keyboard',
    audience: 'inspector',
    lastReviewed: '2026-05-05',
    body: `
| Where | Key | Action |
|---|---|---|
| Anywhere | \`?\` | Open / close help drawer |
| Help drawer | \`Esc\` | Close |
| Help drawer | \`/\` | Focus search |
| Help drawer | \`↑\` / \`↓\` | Cycle search results |
| Help drawer | \`Enter\` | Open highlighted result |
| Demo walkthrough | \`→\` | Next step |
| Demo walkthrough | \`←\` | Previous step |
| Run page | \`y\` / \`n\` / \`a\` | Answer focused question Yes / No / N/A |
| Run page | \`j\` / \`k\` | Next / previous question |
| Run page | \`/\` | Search questions |
| Review page | \`g\` then \`d\` | Generate AI draft |
| Review page | \`s\` | Sign |

Shortcuts are disabled when typing in an input or textarea — so dictating a remark with the letter "y" doesn't accidentally answer the next question.
- v or 4 — answer **N/V** (Not Verified; RISQ questions only)

`,
  },
  {
    slug: 'getting-started/fleet-readiness-score',
    topic: 'getting-started',
    title: 'Fleet readiness score explained',
    summary: 'How the 0-100 Vessel Readiness Score is computed and what moves it.',
    icon: 'Radar',
    audience: 'all',
    lastReviewed: '2026-07-03',
    contexts: ['fleet', 'landing'],
    body: `
Every vessel on the platform carries a **Vessel Readiness Score** — a 0-100 triage signal recomputed nightly and shown identically on the fleet dashboard, vessel search, the live map, and each profile.

## How it's calculated

Start at 100, subtract penalties:

- **Port State Control (24-month window)** — any detention −25, each additional −10; high deficiency density (avg > 3 per inspection) up to −15; any open deficiency −5.
- **Class** — suspended or withdrawn −30; expired −20; expiring within 60 days −10.
- **Age** — 25+ years −10; 20+ years −5.
- **Sanctions** — any active listing −40.
- **Open corrective actions** — −3 each, capped at −15. Closing findings on the Actions board restores these points.

Bands: **85+ Ready** · **65-84 Watch** · **45-64 Concerns** · **below 45 High risk**.

## What it is — and isn't

The score is an *auditable triage signal*: every factor and penalty is shown on the vessel profile ("Show how this is calculated"). It is **not** a charter decision or a condition survey — it tells you which vessels in your fleet need attention this week.
## Beside the score

Two companion indicators sit next to readiness: the fleet's **TMSA 3 average stage** (the company self-assessment under Comply) and each vessel's **estimated Paris MoU ship risk profile** (HRS / SRS / LRS) on the map drawer's Risk tab — computed from ship type, age, flag standing and 36-month PSC history, with a link to the official THETIS calculator.

`,
    related: ['getting-started/welcome'],
  },
  {
    slug: 'post-inspection/corrective-actions',
    topic: 'reporting-signing',
    title: 'Corrective actions lifecycle',
    summary: 'Turning observations into tracked, evidenced closures.',
    icon: 'ListChecks',
    audience: 'all',
    lastReviewed: '2026-07-03',
    contexts: ['actions', 'vetting/inspection-review'],
    body: `
Observations recorded during an inspection don't end at the signed report. From the review page, **Create corrective actions** turns every observation into a tracked finding on the **Actions** board.

## The lifecycle

1. **Open** — created from an observation (severity defaults from the CDI category: Statutory findings start high) or added manually.
2. **In progress** — someone owns it; set a due date so overdue findings surface on the fleet dashboard.
3. **Verified** — the fix is done and checked.
4. **Closed** — requires closure notes referencing evidence (photos, certificates, work orders). Closed actions can be reopened if the problem recurs.

## Why it matters

Open corrective actions lower the vessel's readiness score (−3 each, capped −15) until they're closed — so the fleet dashboard always reflects unresolved findings, and closing work is visibly rewarded.
`,
    related: ['getting-started/fleet-readiness-score'],
  },
  {
    slug: 'standards-vetting/tmsa-self-assessment',
    topic: 'standards-vetting',
    title: 'TMSA 3 self-assessment',
    summary: 'The fleet-level OCIMF framework: 13 elements, stages 1–4.',
    icon: 'Gauge',
    audience: 'all',
    lastReviewed: '2026-07-07',
    contexts: ['tmsa'],
    body: `
Inspections measure the ship; **TMSA measures the company running it**. OCIMF's Tanker Management and Self Assessment sets out 13 management-practice elements, each attained at a stage from 1 (documented) to 4 (industry best practice).

## In VarunAIq

Open **Comply → TMSA self-assessment**. For each element, record the stage you've attained, the stage you're targeting, and evidence notes. The fleet dashboard shows your average stage beside vessel readiness.

## Ground rules

- Stages are sequential — don't claim stage 3 if stage 1 isn't embedded.
- Detailed KPIs live in the OCIMF TMSA 3 publication; record your evidence per element here.
- Charterers increasingly ask for TMSA stage evidence during vetting — keeping this current shortens those conversations.
`,
    related: ['operator/fleet-readiness'],
  },
  {
    slug: 'getting-started/map-layers',
    topic: 'getting-started',
    title: 'Live map — radar, seamarks, weather & photos',
    summary: 'Environmental overlays and per-vessel conditions on the live map.',
    icon: 'Map',
    audience: 'all',
    lastReviewed: '2026-07-07',
    contexts: ['map'],
    body: `
The live map carries two toggleable overlays (top-right):

- **Radar** — live precipitation composite (RainViewer), refreshed ~10-minutely.
- **Seamarks** — buoys, lights and harbour marks from OpenSeaMap.

## Per-vessel intelligence

Click any vessel to open the drawer:

- A **photograph** of the ship when one exists on Wikimedia Commons (credit and licence shown — coverage is partial, biggest ships first).
- **Conditions at position** on the Voyage tab: wind, gusts, waves, swell, air temperature and visibility at the vessel's live AIS position (Open-Meteo).
- The **Risk tab** adds the estimated Paris MoU ship risk profile alongside the VarunAIq readiness score.
`,
    related: ['operator/fleet-readiness'],
  },
  {
    slug: 'operator/self-inspections',
    topic: 'app-features',
    title: 'Self-inspections — internal audits with a gap list',
    summary: 'Run the same CDI/RISQ bank internally; No answers become corrective actions.',
    icon: 'ClipboardCheck',
    audience: 'all',
    lastReviewed: '2026-07-09',
    contexts: ['vetting-new', 'vetting-run', 'fleet'],
    body: `
A **self-inspection** is the same questionnaire your vessel will face at the real
vetting, run internally — by a superintendent during a port call or by ship staff
on board (offline mode works without network).

## Why it exists

- **ISM Code §12** requires internal safety audits at intervals not exceeding
  12 months. The fleet dashboard flags vessels past that cadence.
- **CDI O.10** asks whether a Self Inspection Document was completed before the
  inspector arrived — a submitted self-inspection *is* that document.
- **OCIMF recommends** re-taking the TMSA self-assessment every 12 months; the
  TMSA card on the fleet dashboard tracks that clock too.

## How it works

1. Start one from a vessel profile (**Self-inspection**) or New inspection → mode
   toggle. Deep link: \`/vetting/inspections/new?kind=self\`.
2. Run it exactly like a vetting — same questions, evidence, notes, offline sync.
3. On submit, **every scoreable No answer becomes an open corrective action** in
   Actions, with the question ref and your remark attached. Info-only rows (NS)
   are recorded as facts, not gaps.
4. The report renders as **SELF INSPECTION REPORT — INTERNAL PREPARATION**, so it
   can't be confused with an external SIR.

Close the actions before the charterer's inspector boards — that's the whole point.
`,
    related: ['operator/fleet-readiness', 'operator/tmsa'],
  },
];

export const ARTICLES_BY_SLUG: Record<string, HelpArticle> =
  Object.fromEntries(ARTICLES.map((a) => [a.slug, a]));

export function articlesByTopic(topic: HelpTopic): HelpArticle[] {
  return ARTICLES.filter((a) => a.topic === topic);
}

export function articlesForContext(contextKey: string | null): HelpArticle[] {
  if (!contextKey) return [];
  return ARTICLES.filter((a) => a.contexts?.includes(contextKey));
}

/** Naive but good-enough search at this scale (~30 articles). */
export function searchArticles(query: string): HelpArticle[] {
  const q = query.trim().toLowerCase();
  if (!q) return [];
  return ARTICLES
    .map((a) => {
      const hay = `${a.title}\n${a.summary}\n${a.body}`.toLowerCase();
      let score = 0;
      if (a.title.toLowerCase().includes(q)) score += 10;
      if (a.summary.toLowerCase().includes(q)) score += 5;
      if (hay.includes(q)) score += 1;
      return { a, score };
    })
    .filter((x) => x.score > 0)
    .sort((a, b) => b.score - a.score)
    .slice(0, 12)
    .map((x) => x.a);
}
